/**
 * Scroll an explorer category section to the top of the results.
 *
 * Desktop: the search sits at the very top and the rail is a side column, so
 * the section's CSS `scroll-margin-top` already lands the heading correctly —
 * defer to native `scrollIntoView`.
 *
 * Mobile: search + filters + categories pin as one tall sticky toolbar, and its
 * height is not constant (a "clear all" row appears while a filter is active),
 * so a fixed `scroll-margin` lands the heading *under* the toolbar. Measure the
 * live toolbar bottom instead — the rail's CSS sticky `top` plus its current
 * height — and land the heading 8px below it.
 */
export function scrollSectionToTop(id: string, rail: HTMLElement | null) {
  const el = document.getElementById(id)
  if (!el) return

  const isMobile = window.matchMedia("(max-width: 767px)").matches
  if (isMobile && rail) {
    const railStickyTop = parseFloat(getComputedStyle(rail).top) || 0
    const pinnedBottom = railStickyTop + rail.offsetHeight
    const target =
      window.scrollY + el.getBoundingClientRect().top - pinnedBottom - 8
    window.scrollTo({ top: Math.max(0, target), behavior: "smooth" })
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" })
  }
}

/** Approximate bottom of an explorer's pinned search band, measured from the
 *  viewport top. The results card is landed just below it. */
const PINNED_BAND_HEIGHT = 160

/**
 * Bring an explorer's results card back under the pinned search band — but only
 * ever scrolling UP.
 *
 * Narrowing the list (a text search, a package filter) makes the document
 * shorter. The browser then clamps scroll to the new maximum, so a visitor who
 * had scrolled into the list is left at the very end of the page, below the
 * results entirely. Re-landing the card puts them back on the first result.
 *
 * Upward-only is what keeps this from being its own annoyance: selecting a
 * filter while already at the top of the page, or widening the list again, never
 * yanks the viewport downward.
 *
 * `scrollY + rect.top` is the card's absolute document offset, which doesn't
 * depend on whether the browser has applied its clamp yet — so this is correct
 * whichever side of the clamp the effect runs on.
 */
export function scrollResultsIntoView(card: HTMLElement | null) {
  if (!card) return
  const target = Math.max(
    0,
    window.scrollY + card.getBoundingClientRect().top - PINNED_BAND_HEIGHT
  )
  if (window.scrollY > target + 1)
    window.scrollTo({ top: target, behavior: "smooth" })
}

/**
 * Filter-change variant of `scrollResultsIntoView`: re-land the card only when
 * the visitor has been left entirely past it (the browser clamped scroll to the
 * shorter document). A visitor still looking at the list keeps their position.
 */
export function scrollResultsIfPast(card: HTMLElement | null) {
  if (!card || card.getBoundingClientRect().bottom > 0) return
  scrollResultsIntoView(card)
}
